import { inject, Injectable } from '@angular/core';
import {
  DoodAnonymousUserResponse,
  DoodUserModel,
} from '@store/authentication/authentication.model';
import { DOOD_API } from '@config/ws.config';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { Cb2PreviewStateService } from '@core/services/cb2-preview/cb2-preview-state.service';

@Injectable({
  providedIn: 'root',
})
export class UserApiService {
  private readonly httpClient = inject(HttpClient);
  private readonly marketplaceStoreSelector = inject(MarketplaceStoreSelector);
  private readonly cb2PreviewState = inject(Cb2PreviewStateService);

  getUserApi$(): Observable<DoodUserModel> {
    if (this.cb2PreviewState.isActive()) {
      return throwError(() => new Error('[CB2 preview] skipped GET /users/me'));
    }
    return this.httpClient.get<DoodUserModel>(`${DOOD_API}/users/me`);
  }

  async getUserPromise(): Promise<DoodUserModel | undefined> {
    if (this.cb2PreviewState.isActive()) {
      return undefined;
    }
    return new Promise<undefined | DoodUserModel>(resolve => {
      this.httpClient
        .get<DoodUserModel>(`${DOOD_API}/users/me`)
        .toPromise()
        .then(user => {
          resolve(user);
        })
        .catch(() => {
          resolve(undefined);
        });
    });
  }

  createUserApi$(user: Partial<DoodUserModel>): Observable<DoodUserModel> {
    return this.httpClient.post<DoodUserModel>(`${DOOD_API}/users`, user);
  }

  /**
   * Create a guest user. Must be called without Authorization
   * (Dood anonymous JWT cannot be used to create another anonymous user).
   */
  createAnonymousUserApi$(): Observable<DoodAnonymousUserResponse> {
    if (this.cb2PreviewState.isActive()) {
      return throwError(() => new Error('[CB2 preview] skipped POST /users/anonymous'));
    }
    return this.httpClient.post<DoodAnonymousUserResponse>(`${DOOD_API}/users/anonymous`, {});
  }

  /**
   * Upgrade a Dood guest to a real Firebase-backed account.
   * Authorization (Dood JWT) comes from the interceptor; Firebase ID token is sent separately.
   */
  upgradeAnonymousUserApi$(
    user: Partial<DoodUserModel>,
    id: string,
    firebaseIdToken: string,
  ): Observable<DoodUserModel> {
    const headers = new HttpHeaders({
      'Firebase-Authorization': `Bearer ${firebaseIdToken}`,
    });
    return this.httpClient.patch<DoodUserModel>(
      `${DOOD_API}/users/${id}/upgrade-anonymous`,
      user ?? {},
      { headers },
    );
  }

  updatePersonalInfo$(user: Partial<DoodUserModel>, id: string): Observable<DoodUserModel> {
    return this.httpClient.patch<DoodUserModel>(`${DOOD_API}/users/` + id, user);
  }

  sendUserEmailVerification$(redirectTo: string) {
    return this.httpClient.post<null>(`${DOOD_API}/plugins/user-email-verification/send`, {
      'marketplace_id': this.marketplaceStoreSelector.selectMarketplaceId(),
      'redirect_to': redirectTo,
    });
  }
}
